import Bugsnag from '@bugsnag/js'
import { Button, Grid, InputAdornment, TextField, Typography } from '@mui/material'
import { DateField } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { MdPhoneEnabled } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { client } from '../../utils/client'
import { isStepValid } from '../../utils/misc'
import { FormType } from '../../utils/sharedTypes'
import { phoneNumberValidator } from '../../utils/validators'
import MetropolitanCvNote from '../MetropolitanCvNote'

type CpfStatusYesPropsType = {
  form: FormType
  setForm: React.Dispatch<React.SetStateAction<FormType>>
}

const CpfStatusYes = ({ form, setForm }: CpfStatusYesPropsType) => {
  const navigate = useNavigate()

  const submitFinancement = () => {
    client
      .patch(`/registration/${form.data.global_id}/submit/`, {
        ...form.data,
        appointment_dt: null,
      })
      .then((resp) => {
        if (resp.status === 200) {
          if (
            form.data.custom_processes_validation &&
            form.data.pre_registration_custom_processes.some((p) => !p.validated)
          ) {
            navigate({ pathname: './../questionnaire/', search: location.search })
          } else {
            navigate({ pathname: './../cpf/', search: location.search })
          }
        }
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
      })
  }

  const nextThirthyBusinessDays = Array.from({ length: 30 }, (_, i) => {
    const tmp = new Date()
    tmp.setDate(tmp.getDate() + i)
    return tmp
  }).filter((day) => {
    return day.getDay() !== 0 && day.getDay() !== 6
  })

  const listOfDays = nextThirthyBusinessDays.filter((_, index) => {
    return index < 14
  })

  const handleChange = (key: string, value: string | boolean | Record<string, string>) => {
    setForm((prevState) => {
      const errors = prevState.errors
      delete errors[key]

      switch (key) {
        case 'cpf_contact_mobile_phone':
          if (!phoneNumberValidator(value as string)) {
            errors['cpf_contact_mobile_phone'] = 'Veuillez renseigner un numéro de téléphone valide'
          }
          break
        default:
          break
      }

      return {
        ...prevState,
        data: {
          ...prevState.data,
          [key]: value,
        },
        errors: errors,
      }
    })
  }

  return (
    <>
      <Grid>
        <Typography variant="body1" fontWeight="500">
          Merci de renseigner le numéro de téléphone <u>lié à votre CPF</u>
        </Typography>

        <TextField
          type="tel"
          fullWidth
          id="cpf_contact_mobile_phone"
          label="N° de mobile"
          variant="outlined"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <MdPhoneEnabled size="20" />
              </InputAdornment>
            ),
          }}
          value={form.data.cpf_contact_mobile_phone}
          onChange={(e: React.FocusEvent<HTMLInputElement>) => {
            handleChange('cpf_contact_mobile_phone', e?.target.value)
          }}
          error={Boolean(form.errors.cpf_contact_mobile_phone)}
          helperText={form.errors.cpf_contact_mobile_phone}
          sx={{
            my: 2,
          }}
        />

        <Typography variant="body1" fontWeight="500">
          A quelle date souhaitez-vous démarrer votre formation ? (date minimale :{' '}
          {new Date(listOfDays[listOfDays.length - 1].setDate(listOfDays[listOfDays.length - 1].getDate() + 1))
            .toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })
            .replace(/\s/g, '\u00A0')}
          )
        </Typography>
        <MetropolitanCvNote form={form} />
        <DateField
          id="cpf_starting_date"
          label="Date début de session"
          format="DD/MM/YYYY"
          disablePast
          required
          value={form.data.cpf_starting_date ? dayjs(form.data.cpf_starting_date) : null}
          onChange={(value) => {
            if (value && dayjs(value).isValid() && `${dayjs(value).year()}`.length === 4) {
              handleChange('cpf_starting_date', dayjs(value).format('YYYY-MM-DD'))

              if (!dayjs(value).isValid()) {
                setForm((prevState) => {
                  return {
                    ...prevState,
                    errors: {
                      ...prevState.errors,
                      cpf_starting_date: 'Veuillez entrer une date de session valide',
                    },
                  }
                })
              }

              if (dayjs(value).isBefore(dayjs(listOfDays[listOfDays.length - 1]), 'days')) {
                setForm((prevState) => {
                  return {
                    ...prevState,
                    errors: {
                      ...prevState.errors,
                      cpf_starting_date: "La date de session doit être au moins 14 jours ouvrés après aujourd'hui",
                    },
                  }
                })
              }
            }
          }}
          fullWidth
          helperText={form.errors.cpf_starting_date}
          slots={{
            textField: (params) => (
              <TextField
                {...params}
                fullWidth
                error={Boolean(form.errors.cpf_starting_date)}
                helperText={form.errors.cpf_starting_date}
              />
            ),
          }}
          sx={{
            my: 2,
          }}
        />

        <Grid item sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button
            variant="contained"
            sx={{
              height: {
                xs: 45,
              },
            }}
            disabled={!isStepValid('financement', form)}
            onClick={submitFinancement}
          >
            Valider
          </Button>
        </Grid>
      </Grid>
    </>
  )
}

export default CpfStatusYes
